import { useBundle } from '@amzn/react-arb-tools';
import { BENTO_CARD_VARIANT, HEADING_TAG, ICON_NAME, SECTION_THEME, useDeviceSize } from '@amzn/sss-website-theme';

import { SegmentConfigProps } from 'src/components/pages/segment/types';
import { RESTORING_FORESTS_BUNDLE_NAME } from 'src/constants/panther/resource-bundles';
import { getImageRoot } from 'src/helpers';
import { t } from 'src/helpers/translations/translation-helpers';

export function useRestoringForestsConfig(): { isBundleLoading: boolean; config: SegmentConfigProps } {
  const { isSmallDesktop } = useDeviceSize();
  const imageRoot = getImageRoot(isSmallDesktop);

  const [restoringForestsBundle, isRestoringForestsBundleLoading] = useBundle(RESTORING_FORESTS_BUNDLE_NAME);

  return {
    isBundleLoading: isRestoringForestsBundleLoading,
    config: {
      heroAsset: {
        image: {
          src: `${imageRoot}/credits-segments-restoration-hero-1.jpg`,
          alt: t(restoringForestsBundle, 'heroAsset_image_altText'),
        },
        title: t(restoringForestsBundle, 'heroAsset_title'),
      },

      headingSection: {
        tagline: t(restoringForestsBundle, 'headingSection_tagline'),
        heading: t(restoringForestsBundle, 'headingSection_heading'),
        description: t(restoringForestsBundle, 'headingSection_description'),
      },

      cardsDashboard: {
        ...(isSmallDesktop
          ? {
              firstCard: {
                src: `${imageRoot}/credits-segments-restoration-thechallenge-1.jpg`,
                alt: t(restoringForestsBundle, 'cardsDashboard_firstCard_altText'),
              },
            }
          : {}),
        secondCard: {
          image: {
            src: `${imageRoot}/credits-segments-restoration-thechallenge-2.jpg`,
            alt: t(restoringForestsBundle, 'cardsDashboard_secondCard_altText'),
          },
          label: t(restoringForestsBundle, 'cardsDashboard_secondCard_label'),
          title: t(restoringForestsBundle, 'cardsDashboard_secondCard_title'),
          description: t(restoringForestsBundle, 'cardsDashboard_secondCard_description'),
          variant: BENTO_CARD_VARIANT.STANDARD,
        },
        thirdCard: {
          label: t(restoringForestsBundle, 'cardsDashboard_thirdCard_label'),
          title: t(restoringForestsBundle, 'cardsDashboard_thirdCard_title'),
          description: t(restoringForestsBundle, 'cardsDashboard_thirdCard_description'),
        },
        ...(isSmallDesktop
          ? {
              fourthCard: {
                src: `${imageRoot}/credits-segments-restoration-thechallenge-3.jpg`,
                alt: t(restoringForestsBundle, 'cardsDashboard_fourthCard_altText'),
              },
            }
          : {}),
      },

      dynamicList: {
        mediaProps: {
          mediaTitle: t(restoringForestsBundle, 'dynamicList_mediaTitle'),
          mediaTagline: t(restoringForestsBundle, 'dynamicList_mediaTagline'),
          media: [
            {
              image: {
                src: `${imageRoot}/credits-segments-restoration-ourapproach-1.jpg`,
                alt: t(restoringForestsBundle, 'dynamicList_firstMedia_altText'),
              },
              byLine: t(restoringForestsBundle, 'dynamicList_firstMedia_byLine'),
            },
            {
              image: {
                src: `${imageRoot}/credits-segments-restoration-ourapproach-2.jpg`,
                alt: t(restoringForestsBundle, 'dynamicList_secondMedia_altText'),
              },
            },
          ],
        },
        items: [
          {
            title: t(restoringForestsBundle, 'dynamicList_firstItem_title'),
            body: t(restoringForestsBundle, 'dynamicList_firstItem_body'),
            tooltipAttributes: [
              {
                description: t(restoringForestsBundle, 'dynamicList_firstItem_tooltip_description'),
              },
            ],
          },
          {
            title: t(restoringForestsBundle, 'dynamicList_secondItem_title'),
            body: t(restoringForestsBundle, 'dynamicList_secondItem_body'),
          },
        ],
      },

      secondaryBentoCards: {
        firstCard: `${imageRoot}/credits-segments-restoration-ourquality-1.jpg`,
        firstCardModal: `${imageRoot}/credits-segments-restoration-ourquality-2.jpg`,
        secondCard: `${imageRoot}/credits-segments-restoration-ourquality-3.jpg`,
        secondCardModal: `${imageRoot}/credits-segments-restoration-ourquality-4.jpg`,

        bentoCardProps: {
          label: t(restoringForestsBundle, 'secondaryBentoCards_label'),
          title: t(restoringForestsBundle, 'secondaryBentoCards_title'),
          titleSize: HEADING_TAG.H5,
          description: t(restoringForestsBundle, 'secondaryBentoCards_description'),
          tooltipAttributes: [
            {
              description: t(restoringForestsBundle, 'secondaryBentoCards_tooltip_description'),
              cta: {
                label: t(restoringForestsBundle, 'secondaryBentoCards_tooltip_linkLabel'),
                icon: ICON_NAME.CHEVRON_TOP_RIGHT,
                url: 'https://verra.org/verra-launches-abacus-label-for-ecosystem-restoration-and-reforestation-credits',
              },
            },
          ],
          tooltipTheme: SECTION_THEME.Light,
          firstCard: {
            label: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_label'),
            title: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-restoration-ourquality-1.jpg`,
              alt: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_altText'),
            },
            modal: {
              label: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_label'),
              title: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_title'),
              image: {
                src: `${imageRoot}/credits-segments-restoration-ourquality-2.jpg`,
                alt: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_modal_altText'),
              },
              modalContent: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_modal_content'),
              link: {
                url: 'https://www.amazon.science/blog/five-ways-the-abacus-label-advances-nature-based-carbon-removal',
                label: t(restoringForestsBundle, 'secondaryBentoCards_firstCard_modal_linkLabel'),
                icon: ICON_NAME.LIBRARY,
                isSource: false,
              },
            },
          },
          secondCard: {
            label: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_label'),
            title: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_title'),
            image: {
              src: `${imageRoot}/credits-segments-restoration-ourquality-3.jpg`,
              alt: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_altText'),
            },
            modal: {
              image: {
                src: `${imageRoot}/credits-segments-restoration-ourquality-4.jpg`,
                alt: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_modal_altText'),
              },
              label: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_label'),
              title: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_title'),
              modalContent: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_modal_content'),
              link: {
                url: 'https://www.amazon.science/blog/five-ways-the-abacus-label-advances-nature-based-carbon-removal',
                label: t(restoringForestsBundle, 'secondaryBentoCards_secondCard_modal_linkLabel'),
                icon: ICON_NAME.LIBRARY,
                isSource: false,
              },
            },
          },
        },
      },
    },
  };
}
